import { SmartTableColumn, WithNAFallback } from '@toggle/design-system';
import { generatePath, getPriceStatus } from '@toggle/helpers';
import {
  CatalystInsight,
  ThemeExposure,
  V2ThemePerformanceItem,
} from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { getKnowledgeGraphSearchParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useCatalystInsightsList } from '~/react-query-hooks/insights/useCatalystInsights';
import { useCompanyTheme } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useGetV2ThemePerformance } from '~/react-query-hooks/themes/useGetV2ThemePerformance';
import { appPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { priceChangeColors } from '~/utils/common/utils';
import { ThemeLogoIcon } from '~/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { formatPercentageNumber } from '~/widgets/earnings/utils/utils';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { useThemeDrawer } from '~/widgets/theme-widget/theme-drawer/useThemeDrawer';

import { SectionBase } from '../../shared/section-base/SectionBase';
import styles from './RevampThemeKnowledgeGraph.module.scss';
import { ThemeCatalystsCell } from './ThemeCatalystsCell';

type ThemeExposureTableRow = ThemeExposure & {
  priceKey: ThemeExposure['theme'];
  catalystKey: ThemeExposure['theme'];
};

export interface RevampThemeKnowledgeGraphProps {
  entityTag: string;
  isInDrawer?: boolean;
}

export const RevampThemeKnowledgeGraph = ({
  entityTag,
  isInDrawer = false,
}: RevampThemeKnowledgeGraphProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openDrawer } = useThemeDrawer();
  const { trackThemeOpen, trackThemeOverviewDrawerOpen } = usePosthogEvents();

  const {
    data = [],
    isFetching,
    isError: isThemeError,
  } = useCompanyTheme({
    entityTag,
    limit: 3,
  });

  const themeIds = data.map(d => d.theme.id);
  const {
    data: v2ThemePerformance,
    isFetching: isV2ThemePerformanceFetching,
    isError: isV2ThemePerformanceError,
  } = useGetV2ThemePerformance({
    ids: themeIds.join(','),
    intraday: true,
  });

  const shouldShowMarketCatalysts = useMemo(
    () => themeIds.length > 0,
    [themeIds]
  );
  const {
    data: catalystInsights = [],
    isFetching: isCatalystFetching,
    isError: isCatalystError,
  } = useCatalystInsightsList({ themes: themeIds }, shouldShowMarketCatalysts);

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.ThemeExposure,
  }).toString();
  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  const onClickHandler = isInDrawer
    ? (themeId: string) => {
        trackThemeOpen(POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW);
        const requestPath = generatePath({
          basePath: `${appPaths.themeOverview}/${themeId}`,
          params: {
            [queryKeys.activeSubTab]: 'overview',
          },
        });
        navigate(requestPath);
      }
    : (themeId: string) => {
        trackThemeOverviewDrawerOpen(POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW);
        openDrawer(themeId);
      };

  const themePerformanceMap = useMemo(
    () =>
      new Map<string, V2ThemePerformanceItem>(
        v2ThemePerformance?.themes?.map(theme => [theme.id, theme]) ?? []
      ),
    [v2ThemePerformance?.themes]
  );

  const catalystInsightsMap = useMemo(() => {
    const map = new Map<string, CatalystInsight[]>();
    catalystInsights.forEach(insight => {
      const existing = map.get(insight.identifier) ?? [];
      map.set(insight.identifier, [...existing, insight]);
    });
    return map;
  }, [catalystInsights]);

  const marketCatalystColumn:
    | SmartTableColumn<ThemeExposureTableRow>
    | undefined = useMemo(
    () =>
      shouldShowMarketCatalysts
        ? {
            label: '',
            key: 'catalystKey',
            alignment: 'right',
            render: value => {
              const themeCatalystInsights =
                catalystInsightsMap.get(value.id) ?? [];

              return (
                <ThemeCatalystsCell
                  catalystInsights={themeCatalystInsights}
                  source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
                />
              );
            },
          }
        : undefined,
    [shouldShowMarketCatalysts, catalystInsightsMap]
  );

  const tableColumns: SmartTableColumn<ThemeExposureTableRow>[] = useMemo(
    () => [
      {
        label: t('widget:assetOverview.knowledgeGraphRevamp.columns.theme'),
        key: 'theme',
        render: value => (
          <div className={styles.themeNameWrapper}>
            <ThemeLogoIcon
              name={value.name}
              logo_url={value.logo_url}
              isCondensed={false}
              fallbackIcon={
                <div className={styles.logoWrapper}>{value.name.charAt(0)}</div>
              }
            />
            <span className={styles.themeName}>{value.name}</span>
          </div>
        ),
      },
      {
        label: t('widget:assetOverview.knowledgeGraphRevamp.columns.change'),
        key: 'priceKey',
        alignment: 'right',
        render: value => {
          const intradayReturn = themePerformanceMap.get(value.id)?.intraday
            ?.current;
          const status = getPriceStatus(intradayReturn);

          return (
            <div className={styles.changeRow}>
              <WithNAFallback
                check={intradayReturn !== undefined}
                value={
                  <span
                    className={styles.changeValue}
                    style={{ color: priceChangeColors[status] }}
                  >
                    {formatPercentageNumber({
                      value: intradayReturn,
                      includePercentSign: true,
                      addPlus: true,
                    })}
                  </span>
                }
              />
            </div>
          );
        },
      },
    ],
    [t, themePerformanceMap]
  );

  if (isThemeError || isV2ThemePerformanceError || isCatalystError) {
    return <ErrorMessageWrapper displayIcon />;
  }

  return (
    <SectionBase<ThemeExposureTableRow>
      title={t('widget:assetOverview.knowledgeGraphRevamp.themeExposure')}
      tableData={data.map(d => ({
        ...d,
        key: d.theme.id,
        priceKey: d.theme,
        catalystKey: d.theme,
      }))}
      tableColumns={[
        ...tableColumns,
        ...(marketCatalystColumn ? [marketCatalystColumn] : []),
      ]}
      isFetching={
        isFetching || isV2ThemePerformanceFetching || isCatalystFetching
      }
      onExpand={() => navigate(linkToKnowledgeGraph)}
      onRowClick={item => onClickHandler(item.theme.id)}
    />
  );
};
